import type { IFilterManagerModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-manager/types";
import type { FilterInstanceType } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type {
    FilterValueDeltaList,
    FilterValueRemoveFn,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { FilterStateItem } from "./FilterStateItem";
import { StyledFiltersStateList } from "./styles";

export type FiltersStateStrategyProps<T extends IPlainObject = IPlainObject> = {
    isFilterActionEnabled: boolean;
    filtersManager: IFilterManagerModel<T>;
    filtersList: Partial<T>;
    filterValueDeltaList: Readonly<FilterValueDeltaList>;
    onFilterUpdate: (key: keyof T, value: T[keyof T] | undefined) => void;
    onFilterValueRemove: FilterValueRemoveFn<T>;
};

export const FiltersStateStrategy = <T extends IPlainObject = IPlainObject>({
    isFilterActionEnabled,
    filtersList,
    filtersManager,
    filterValueDeltaList,
    onFilterUpdate,
    onFilterValueRemove,
}: FiltersStateStrategyProps<T>) => {
    return (
        <StyledFiltersStateList data-automation="filters-state-list">
            {Object.entries(filtersList).map(([filterKey, filterValue]) => {
                const filterConfig = filtersManager.getFilterConfig(filterKey);

                if (!filterConfig) {
                    return null;
                }

                const {
                    key,
                    title,
                    filter,
                    filterResultView: FilterResultView,
                } = filterConfig as FilterInstanceType<T, unknown, unknown>;

                if (!FilterResultView || !title) {
                    return null;
                }

                const value = filter.fromApiValue(filterValue as T[keyof T]);

                const handleGetLifecycleStatus = <_, V>(valueOrPartialValue: V) => {
                    const valueDelta = filterValueDeltaList[filterKey];

                    return filter.getValueLifecycleStatus(valueOrPartialValue, valueDelta);
                };

                return (
                    <FilterStateItem
                        key={key as string}
                        title={title}
                        filterKey={String(key)}
                    >
                        <FilterResultView
                            filter={filter}
                            value={value}
                            isExpanded={false}
                            isActionEnabled={isFilterActionEnabled}
                            onGetLifecycleStatus={handleGetLifecycleStatus}
                            onFilterUpdate={(v?: T[keyof T]) => onFilterUpdate(key, v)}
                            onFilterValueRemove={(v?: T[keyof T]) => onFilterValueRemove(key, v, value as T[keyof T])}
                        />
                    </FilterStateItem>
                );
            })}
        </StyledFiltersStateList>
    );
};
